import { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import {
  isValidPhoneNumber,
  isPossiblePhoneNumber,
} from "react-phone-number-input/max";
import "react-phone-number-input/style.css";
import styles from "./Login.module.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  loginUser,
  resendOtp,
  clearError,
  clearOtpState,
} from "../../../store/slices/authSlice";
import { MdOutlineLanguage } from "react-icons/md";
import { toast } from "react-toastify";

const DEFAULT_COUNTRY = "EG";

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { loading, googleLoading, error, isAuthenticated, otpLoading } =
    useSelector((s) => s.auth);

  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ phone: "", password: "" });
  const [showResendBlock, setShowResendBlock] = useState(false);
  const [showNotFoundBlock, setShowNotFoundBlock] = useState(false);

  const stripTashkeel = (str) => str.replace(/[\u064B-\u065F\u0670]/g, "");

  const UNVERIFIED_KEYWORDS = [
    "not vearified",
    "unverified",
    "not activated",
    "غير مفعل",
    "غير محقق",
    "لم يتم التحقق",
    "account is not verified",
    "email not verified",
    "phone not verified",
  ];

  const WRONG_PASSWORD_KEYWORDS = [
    "invalid password",
    "incorrect password",
    "wrong password",
    "invalid credentials",
    "كلمة المرور غير صحيحة",
    "كلمة السر غير صحيحة",
  ];

  const NOT_FOUND_KEYWORDS = [
    "not found",
    "does not exist",
    "doesn't exist",
    "user not found",
    "no account",
    "غير مسجل",
    "غير موجود",
    "لا يوجد",
  ];

  const getPhoneError = (value) => {
    if (!value) return "رقم الهاتف مطلوب";

    if (!isPossiblePhoneNumber(value)) {
      return "رقم الهاتف غير مكتمل، تأكد من عدد الأرقام";
    }

    if (!isValidPhoneNumber(value)) {
      return "رقم الهاتف غير صحيح، تأكد من كتابته بشكل سليم";
    }

    return "";
  };

  const isPhoneValid = phone && !getPhoneError(phone);

  const getErrorMessage = (err) => {
    if (!err) return "";
    return typeof err === "object"
      ? stripTashkeel(err.message?.toLowerCase() || "")
      : stripTashkeel(err.toLowerCase());
  };

  const isWrongPasswordError = (err) => {
    const msg = getErrorMessage(err);
    if (!msg) return false;
    return WRONG_PASSWORD_KEYWORDS.some((kw) =>
      msg.includes(stripTashkeel(kw.toLowerCase())),
    );
  };

  const isUnverifiedError = (err) => {
    const msg = getErrorMessage(err);
    if (!msg) return false;
    return UNVERIFIED_KEYWORDS.some((kw) =>
      msg.includes(stripTashkeel(kw.toLowerCase())),
    );
  };

  const isAccountNotFoundError = (err) => {
    const msg = getErrorMessage(err);
    if (!msg) return false;
    return NOT_FOUND_KEYWORDS.some((kw) =>
      msg.includes(stripTashkeel(kw.toLowerCase())),
    );
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    toast.success(t("common.welcome"), {
      autoClose: 1500,
      position: "top-center",
    });
    navigate(location.state?.from?.pathname || "/");
  }, [isAuthenticated]);

  useEffect(() => {
    if (!error) return;

    if (isWrongPasswordError(error)) {
      setShowResendBlock(false);
      setShowNotFoundBlock(false);
      const msg = typeof error === "object" ? error.message : error;
      setErrors((p) => ({ ...p, password: msg || "بيانات الدخول غير صحيحة" }));
      dispatch(clearError());
      return;
    }

    if (isUnverifiedError(error)) {
      setShowNotFoundBlock(false);
      setShowResendBlock(true);
      dispatch(clearError());
      return;
    }

    if (isAccountNotFoundError(error)) {
      setShowResendBlock(false);

      const phoneErr = getPhoneError(phone);
      if (phoneErr) {
        setShowNotFoundBlock(false);
        setErrors((p) => ({ ...p, phone: phoneErr }));
      } else {
        setShowNotFoundBlock(true);
      }

      dispatch(clearError());
      return;
    }

    setShowResendBlock(false);
    setShowNotFoundBlock(false);
    const msg = typeof error === "object" ? error.message : error;
    setErrors((p) => ({ ...p, password: msg || "بيانات الدخول غير صحيحة" }));
    dispatch(clearError());
  }, [error, dispatch, phone]);

  const handlePhoneChange = (value) => {
    setPhone(value || "");
    setShowResendBlock(false);
    setShowNotFoundBlock(false);
    if (errors.phone) setErrors((p) => ({ ...p, phone: "" }));
  };

  const handlePhoneBlur = () => {
    if (!phone) return;
    const phoneErr = getPhoneError(phone);
    setErrors((p) => ({ ...p, phone: phoneErr }));
  };

  const validate = () => {
    const newErrors = {
      phone: getPhoneError(phone),
      password: !password.trim() ? "كلمة المرور مطلوبة" : "",
    };
    setErrors(newErrors);
    return !newErrors.phone && !newErrors.password;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowResendBlock(false);
    setShowNotFoundBlock(false);
    if (!validate()) return;
    dispatch(
      loginUser({
        phone,
        password,
      }),
    );
  };

  const handleResendOtp = async () => {
    const phoneErr = getPhoneError(phone);
    if (phoneErr) {
      setErrors((p) => ({ ...p, phone: phoneErr }));
      return;
    }

    try {
      await dispatch(resendOtp(phone)).unwrap();

      dispatch(clearOtpState());

      toast.success(t("common.otpSent") || "تم إرسال كود التحقق بنجاح", {
        autoClose: 2000,
        position: "top-center",
      });

      navigate("/verify-otp", { state: { phone } });
    } catch (err) {
      toast.error(
        typeof err === "string" ? err : "فشل إرسال الكود، حاول مرة أخرى",
        { position: "top-center" },
      );
    }
  };

  return (
    <div className={styles.login}>
      <div className={styles.loginContainer}>
        <div className={styles.topBar}>
          <div className="desktop-language-dropdown">
            <button
              className={`btn custom-toggle ${styles.langBtn}`}
              data-bs-toggle="dropdown"
            >
              <MdOutlineLanguage />
              {i18n.language === "ar" ? "اللغه" : "Language"}
            </button>
            <ul className="dropdown-menu">
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    i18n.changeLanguage("ar");
                    localStorage.setItem("lang", "ar");
                  }}
                >
                  {t("navbar.language_ar")}
                </button>
              </li>
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    i18n.changeLanguage("en");
                    localStorage.setItem("lang", "en");
                  }}
                >
                  {t("navbar.language_en")}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className={styles.loginCard}>
          <h2>{t("login.title")}</h2>
          <p className={styles.desc}>{t("login.desc")}</p>

          {showResendBlock && (
            <div className={styles.unverifiedBanner}>
              <div className={styles.unverifiedText}>
                <p className={styles.unverifiedTitle}>
                  {t("common.unverifiedTitle") || "حسابك غير مفعّل"}
                </p>
                <p className={styles.unverifiedSub}>
                  {t("common.unverifiedSub") ||
                    "هل تريد إرسال كود تحقق جديد لرقم هاتفك؟"}
                </p>
              </div>
              <button
                className={styles.resendOtpBtn}
                onClick={handleResendOtp}
                disabled={otpLoading}
                type="button"
              >
                {otpLoading
                  ? t("common.sending") || "جاري الإرسال..."
                  : t("common.resendCode") || "إرسال كود جديد"}
              </button>
            </div>
          )}

          {showNotFoundBlock && isPhoneValid && (
            <div className={styles.unverifiedBanner}>
              <div className={styles.unverifiedText}>
                <p className={styles.unverifiedTitle}>
                  {t("common.noAccountTitle") || "لا يوجد حساب بهذا الرقم"}
                </p>
                <p className={styles.unverifiedSub}>
                  {t("common.noAccountSub") ||
                    "تأكد من الرقم، أو أنشئ حسابًا جديدًا"}
                </p>
              </div>
              <button
                className={styles.resendOtpBtn}
                onClick={() => navigate("/register")}
                type="button"
              >
                {t("login.signup") || "إنشاء حساب"}
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.inputGroup}>
              <label htmlFor="phone">{t("login.phone") || "رقم الهاتف"}</label>

              <div
                className={`${styles.phoneInputWrap} ${
                  errors.phone ? styles.inputError : ""
                }`}
                dir="ltr"
              >
                <PhoneInput
                  id="phone"
                  defaultCountry={DEFAULT_COUNTRY}
                  international
                  countryCallingCodeEditable={false}
                  value={phone}
                  onChange={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  placeholder={t("login.phone") || "رقم الهاتف"}
                />
              </div>

              {errors.phone && (
                <span className={styles.fieldError}>{errors.phone}</span>
              )}
            </div>

            <div className={`${styles.inputGroup} ${styles.passwordGroup}`}>
              <div className={styles.passwordHeader}>
                <label htmlFor="password">{t("login.password")}</label>
                <button
                  type="button"
                  className={styles.forgotLink}
                  onClick={() => navigate("/forgot-password")}
                >
                  {t("login.forgot")}
                </button>
              </div>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password)
                    setErrors((p) => ({ ...p, password: "" }));
                }}
                placeholder={t("login.password")}
                className={errors.password ? styles.inputError : ""}
              />
              <span
                className={styles.togglePassword}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </span>
              {errors.password && (
                <span className={styles.fieldError}>{errors.password}</span>
              )}
            </div>

            <button
              type="submit"
              className={`${styles.button} ${styles.buttonLogin}`}
              disabled={loading}
            >
              {loading ? t("common.loginLoading") : t("login.submit")}
            </button>
          </form>

          <span>{t("login.or")}</span>

          <button
            className={`${styles.button} ${styles.buttonGoogle}`}
            onClick={() => {}}
            disabled={googleLoading}
            type="button"
          >
            {googleLoading ? t("common.googleLoading") : t("login.google")}
          </button>

          <p className={styles.signup}>
            {t("login.noAccount")}{" "}
            <button
              type="button"
              className={styles.joinNow}
              onClick={() => navigate("/register")}
            >
              {t("login.signup")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
